import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Globe, ExternalLink } from 'lucide-react'
import type { ClientItem } from '../sections/home/OurClients'
import { API_BASE } from '../config'
import { SafeImage } from '../components/ui/SafeImage'
import { FaqSection } from '../components/faq/FaqSection'

const CLIENTS_FALLBACK_FAQS = [
  {
    question: 'Which brands and industries work with DigiSutra?',
    answer:
      'We partner with B2B companies, real estate developers, education brands, manufacturing firms, and professional services — businesses where every qualified lead matters.',
  },
  {
    question: 'What results do DigiSutra clients typically see?',
    answer:
      'Clients usually see clearer brand positioning, more consistent enquiry flow, and measurable ROI within 60–90 days once Brand → Lead → Sales systems are running.',
  },
  {
    question: 'Can DigiSutra work with businesses outside Surat?',
    answer:
      'Yes. DigiSutra is based in Surat and serves clients across India remotely — including Mumbai, Ahmedabad, Bangalore, Hyderabad, Silvassa, Vapi, and more.',
  },
  {
    question: 'How do we start working with DigiSutra?',
    answer:
      'Book a strategy call from the Contact page. We audit your current marketing, recommend a focused plan, and share a tailored scope — no one-size-fits-all packages.',
  },
]

export default function ClientsPage() {
  const [clients, setClients] = useState<ClientItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ClientItem | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/api/public/clients`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.clients)) return
        setClients(data.clients)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="container-wide">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">Trusted by</span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-white mt-2 mb-3">
            Our Clients
          </h1>
          <p className="section-sub max-w-2xl mx-auto">
            Businesses across industries trust DigiSutra for growth, lead generation, and digital presence.
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className="h-[200px] sm:h-[220px] rounded-2xl bg-slate-200/50 dark:bg-slate-700/30 animate-pulse"
              />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <p className="text-center text-slate-500 dark:text-slate-400">No clients to show yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {clients.map((client, i) => (
              <motion.button
                key={client.id}
                type="button"
                className="group flex h-full min-h-[200px] sm:min-h-[220px] flex-col rounded-2xl card-glass p-5 sm:p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover dark:hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:focus-visible:ring-accent/50"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                onClick={() => setSelected(client)}
              >
                {/* Fixed logo slot — keeps every card aligned */}
                <div className="flex h-16 sm:h-20 w-full shrink-0 items-center justify-center px-2">
                  <SafeImage
                    src={client.logo}
                    alt={client.name}
                    label={client.name}
                    tone="logo"
                    className="flex h-full w-full max-w-[200px] items-center justify-center"
                    imgClassName="h-full w-full object-contain grayscale transition-all duration-300 group-hover:grayscale-0"
                    loading="lazy"
                  />
                </div>

                {/* Fixed text block — name / industry always same vertical start */}
                <div className="mt-4 flex flex-1 flex-col items-center justify-start gap-1.5 w-full min-w-0">
                  <h2 className="w-full text-sm sm:text-base font-semibold leading-snug text-slate-800 dark:text-slate-100 line-clamp-2 min-h-[2.5rem] sm:min-h-[2.75rem]">
                    {client.name}
                  </h2>
                  <p className="w-full text-xs sm:text-[0.8rem] font-medium text-slate-500 dark:text-slate-400 truncate min-h-[1.15rem]">
                    {client.industry || '\u00A0'}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="container-wide mt-16 sm:mt-20 md:mt-24">
        <FaqSection
          location="clients"
          title="Questions brands ask before partnering"
          subtitle="Trust, results, and how DigiSutra works with client teams — answers that help you decide with clarity."
          fallback={CLIENTS_FALLBACK_FAQS}
          schemaId="clients-faq-ld"
        />
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <motion.div
              className="bg-white dark:bg-navy-900 rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center p-1">
                      <SafeImage
                        src={selected.logo}
                        alt={selected.name}
                        label={selected.name}
                        tone="logo"
                        className="h-full w-full"
                        imgClassName="h-full w-full object-contain"
                      />
                    </div>
                    <div className="min-w-0 text-left">
                      <h2 className="text-xl font-bold text-slate-800 dark:text-white leading-snug">
                        {selected.name}
                      </h2>
                      {selected.industry && (
                        <p className="text-sm text-accent font-medium mt-1">{selected.industry}</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="p-2 rounded-full text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
                    aria-label="Close"
                  >
                    <X size={24} />
                  </button>
                </div>

                {selected.description && (
                  <p className="mt-6 text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed text-left">
                    {selected.description}
                  </p>
                )}

                {selected.websiteUrl && (
                  <a
                    href={selected.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-primary dark:text-accent font-medium hover:underline text-sm"
                  >
                    <Globe size={18} />
                    Visit website
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
